import {
  BuildingLibraryIcon,
  CubeIcon,
  HomeModernIcon,
  ShoppingBagIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";
import { lazy } from "react";

import type { FeatureMetadata } from "../feature.types";
import {
  buildCrudPermissions,
  createLazyPlaceholderPage,
} from "../shared/featureFactories";

const LibraryPage = lazy(() => import("./library/pages/Library"));

const transportPermissions = buildCrudPermissions("Infrastructure", "Transport", {
  create: "Infrastructure.Transport.manage",
  edit: "Infrastructure.Transport.manage",
  delete: "Infrastructure.Transport.manage",
});

const placeholderConfigs = [
  {
    id: "hostel",
    label: "Hostel",
    path: "/hostel",
    icon: HomeModernIcon,
    order: 3,
    permissions: buildCrudPermissions("Infrastructure", "Hostel"),
  },
  {
    id: "inventory",
    label: "Inventory",
    path: "/inventory",
    icon: CubeIcon,
    order: 4,
    permissions: buildCrudPermissions("Infrastructure", "Inventory", {
      create: "Infrastructure.Inventory.manageStock",
      edit: "Infrastructure.Inventory.manageStock",
      delete: "Infrastructure.Inventory.manageStock",
    }),
  },
  {
    id: "canteen",
    label: "Canteen",
    path: "/canteen",
    icon: ShoppingBagIcon,
    order: 5,
    permissions: buildCrudPermissions("Infrastructure", "Canteen", {
      create: "Infrastructure.Canteen.view",
      edit: "Infrastructure.Canteen.view",
      delete: "Infrastructure.Canteen.view",
    }),
  },
  {
    id: "transport",
    label: "Transport",
    path: "/transport",
    icon: TruckIcon,
    order: 1,
    permissions: transportPermissions,
  },
] as const satisfies ReadonlyArray<{
  id: string;
  label: string;
  path: string;
  icon: FeatureMetadata["navigation"]["icon"];
  order: number;
  permissions: FeatureMetadata["permissions"];
}>;

const placeholderFeatures: FeatureMetadata[] = placeholderConfigs
  .filter((config) => config.id !== "transport")
  .map(({ id, label, path, icon, order, permissions }) => ({
    id,
    moduleName: "infrastructure",
    navigation: { label, path, icon, order },
    permissions,
    route: {
      path,
      element: createLazyPlaceholderPage(label),
    },
    readiness: "live",
  }));

const transportFeature: FeatureMetadata = {
  id: "transport",
  moduleName: "infrastructure",
  navigation: {
    label: "Transport",
    path: "/transport",
    icon: TruckIcon,
    order: 1,
  },
  permissions: transportPermissions,
  route: {
    path: "/transport",
    element: createLazyPlaceholderPage("Transport"),
  },
  readiness: "live",
};

const libraryFeature: FeatureMetadata = {
  id: "library",
  moduleName: "infrastructure",
  navigation: {
    label: "Library",
    path: "/library",
    icon: BuildingLibraryIcon,
    order: 2,
  },
  route: {
    path: "/library",
    element: LibraryPage,
  },
  permissions: buildCrudPermissions("Infrastructure", "Library", {
    create: "Infrastructure.Library.issueBooks",
    edit: "Infrastructure.Library.returnBooks",
    delete: "Infrastructure.Library.returnBooks",
  }),
  readiness: "live",
};

export const INFRASTRUCTURE_FEATURES = [
  libraryFeature,
  transportFeature,
  ...placeholderFeatures,
] as const satisfies readonly FeatureMetadata[];
